import { createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import api from '@/utils/api';
import { buildSignBroadcast } from '@/utils/kleverTx';

interface CreateUserProfileData {
  displayName?: string;
  bio?: string;
  profileImage?: File;
  homeLocationId?: string;
}

// interface CreateBlockchainProfileData {
//   creatorId: string;
//   metadataUri: string;
//   priceKlyx?: string;
// }

interface UserProfileResponse {
  message: string;
  profile: {
    userId: string;
    displayName: string;
    avatarUrl: string | null;
    bio: string | null;
    badges: any[];
  };
}

interface BlockchainProfileResponse {
  message?: string;
  txHash?: string;
  expectedProfileId?: number;
  walletAddress?: string;
  instructions?: { note: string };
  // Wallet users: backend returns unsigned tx for frontend signing
  requiresWalletSigning?: boolean;
  payload?: any;
  metadata?: string;
  kdaFee?: string;
  network?: string;
}

/**
 * Create user profile thunk
 *
 * Flow:
 * 1. Creates basic user profile (displayName, bio, avatar) in app.profile table
 * 2. On success, automatically triggers blockchain profile creation
 * 3. If blockchain fails, user can still use the app (retry later)
 */
export const createUserProfile = createAsyncThunk(
  'profile/createUserProfile',
  async (data: CreateUserProfileData, { rejectWithValue }) => {
    try {
      // 1. Create basic user profile
      const formData = new FormData();

      if (data.displayName?.trim()) {
        formData.append('displayName', data.displayName.trim());
      }

      if (data.bio?.trim()) {
        formData.append('bio', data.bio.trim());
      }

      if (data.profileImage) {
        formData.append('profileImage', data.profileImage);
      }

      if (data.homeLocationId) {
        formData.append('homeLocationId', data.homeLocationId);
      }

      // Explicit multipart Content-Type — otherwise axios inherits the
      // shared api instance default (application/json) and the request
      // arrives at the server with the wrong Content-Type, multer can't
      // parse the body, and req.file ends up undefined. Same pattern the
      // working ImageUpload + admin CSV upload pages use.
      const userProfileResponse = await api.post<UserProfileResponse>(
        '/user-profile/create',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );

      console.log('✅ User profile created:', userProfileResponse.data);

      // 2. Create blockchain profile (NFT)
      // Use the public metadata endpoint as the metadataUri
      const creatorId = userProfileResponse.data.profile.userId;
      const metadataUri = `${window.location.origin}/api/user-profile/public/${creatorId}`;

      let blockchainProfile: BlockchainProfileResponse | null = null;
      let blockchainError: string | null = null;

      try {
        const blockchainResponse = await api.post<BlockchainProfileResponse>(
          '/blockchain-profiles',
          { creatorId, metadataUri }
        );

        if (blockchainResponse.data.requiresWalletSigning) {
          // Wallet user: sign the unsigned tx, then confirm with backend
          const { payload, metadata, kdaFee, network } = blockchainResponse.data;
          const txHash = await buildSignBroadcast({ payload, metadata: metadata!, kdaFee, network });
          const confirmResponse = await api.post('/blockchain-profiles/confirm', { txHash });
          blockchainProfile = confirmResponse.data;
        } else {
          blockchainProfile = blockchainResponse.data;
        }

      } catch (bcError: any) {
        // Don't fail the whole operation if blockchain fails
        blockchainError = bcError.response?.data?.error || bcError.message || 'Blockchain profile creation failed';
        toast.error(`Blockchain setup failed: ${blockchainError}`);
      }

      return {
        userProfile: userProfileResponse.data,
        blockchainProfile,
        blockchainError,
      };
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to create profile';
      console.error('❌ Profile creation error:', error);
      return rejectWithValue(message);
    }
  }
);

/**
 * Update user profile thunk
 */
export const updateUserProfile = createAsyncThunk(
  'profile/updateUserProfile',
  async (data: CreateUserProfileData, { rejectWithValue }) => {
    try {
      const formData = new FormData();

      if (data.displayName?.trim()) {
        formData.append('displayName', data.displayName.trim());
      }

      if (data.bio?.trim()) {
        formData.append('bio', data.bio.trim());
      }

      if (data.profileImage) {
        formData.append('profileImage', data.profileImage);
      }

      const response = await api.put<UserProfileResponse>(
        '/user-profile',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );

      console.log('✅ User profile updated:', response.data);
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to update profile';
      console.error('❌ Profile update error:', error);
      return rejectWithValue(message);
    }
  }
);

/**
 * Get user profile thunk
 */
export const getUserProfile = createAsyncThunk(
  'profile/getUserProfile',
  async (_, { rejectWithValue }) => {
    try {
      // GET /user-profile returns the profile fields at the top level
      // (userId, displayName, avatarUrl, bio, ...) — no `{ profile: ... }`
      // wrapper. The response shape matches app.profile columns.
      const response = await api.get<{
        userId: string;
        displayName?: string;
        avatarUrl?: string;
        bannerUrl?: string;
        bio?: string;
        headline?: string;
        badges?: unknown[];
        links?: unknown[];
        interests?: unknown[];
        updatedAt?: string;
      }>('/user-profile');
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to fetch profile';
      return rejectWithValue(message);
    }
  }
);

/**
 * Retry blockchain profile creation
 * Use this if the blockchain profile creation failed during initial setup
 */
export const retryBlockchainProfile = createAsyncThunk(
  'profile/retryBlockchainProfile',
  async (userId: string, { rejectWithValue }) => {
    try {
      // Use the public metadata endpoint as the metadataUri
      const metadataUri = `${window.location.origin}/api/user-profile/public/${userId}`;

      const blockchainResponse = await api.post<BlockchainProfileResponse>(
        '/blockchain-profiles',
        { metadataUri }
      );

      let result: BlockchainProfileResponse;
      if (blockchainResponse.data.requiresWalletSigning) {
        const { payload, metadata, kdaFee, network } = blockchainResponse.data;
        const txHash = await buildSignBroadcast({ payload, metadata: metadata!, kdaFee, network });
        const confirmResponse = await api.post('/blockchain-profiles/confirm', { txHash });
        result = confirmResponse.data;
      } else {
        result = blockchainResponse.data;
      }

      console.log('✅ Blockchain profile created (retry):', result);
      return result;
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to create blockchain profile';
      console.error('❌ Blockchain profile retry error:', error);
      return rejectWithValue(message);
    }
  }
);
